import React, { useEffect, useState, Suspense } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import CartItemsInCart from "../canvas/CartItemsInCart";
import cartAPI from "../../api/cartApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import Summary from "./common/Summary";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { ShoppingBag } from "lucide-react";
import Breadcrump from "../../components/Breadcrump";

function Cart() {
  const { cartItems, fetchCart, cartSummary } = useCart();

  const [couponList, setCouponList] = useState([]);
  const [couponCode, setCouponCode] = useState(cartSummary?.couponCode || "");
  const [activeCoupon, setActiveCoupon] = useState(cartSummary?.couponCode || "");

  useEffect(() => {
    fetchCouponCodes();
  }, []);

  useEffect(() => {
    setCouponCode(cartSummary?.couponCode || "");
    setActiveCoupon(cartSummary?.couponCode || "");
  }, [cartSummary]);

  const fetchCouponCodes = async () => {
    try {
      const res = await cartAPI.showCoupon();
      if (res.code === 200) {
        setCouponList(res.data);
      }
    } catch (err) {
      console.error("Error fetching coupons:", err);
    }
  };

  const applyCoupon = async (codeParam) => {
    const codeToApply = codeParam || couponCode;
    if (!codeToApply.trim()) return showError("Please enter a valid coupon code");

    setActiveCoupon(codeToApply);
    setCouponCode(codeToApply);

    try {
      const res = await cartAPI.applyCoupon({ couponCode: codeToApply });
      if (res.code === 200) {
        await fetchCart();
        showSuccess("Coupon applied successfully");
      } else {
        setActiveCoupon("");
        showError(res.message || "Invalid coupon code");
      }
    } catch (err) {
      setActiveCoupon("");
      handleApiValidationError(err, showError);
    }
  };

  const removeCoupon = async () => {
    try {
      const res = await cartAPI.removeCoupon();
      if (res.code === 200) {
        setActiveCoupon("");
        setCouponCode("");
        await fetchCart();
        showSuccess("Coupon removed");
      }
    } catch (err) {
      console.error("Error removing coupon:", err);
    }
  };

  return (
    <>
      <Breadcrump title="Your Shopping Bag" />

      <div className="luxury-cart-page-wrapper py-5">
        <div className="container">
          {cartItems && cartItems.length > 0 ? (
            <div className="row g-5">
              
              {/* LEFT COLUMN: MAIN PRODUCTS TABLE LIST */}
              <div className="col-xl-8">
                <div className="luxury-cart-table-container">
                  <table className="luxury-editorial-table w-100">
                    <thead>
                      <tr>
                        <th className="text-start">Product</th>
                        <th className="text-center d-none d-sm-table-cell">MRP</th>
                        <th className="text-center">Price</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <Suspense fallback={<tr><td colSpan="5" className="text-center py-4">Loading bag items...</td></tr>}>
                        {cartItems.map((item) => (
                          <CartItemsInCart key={item._id} item={item} />
                        ))}
                      </Suspense>
                    </tbody>
                  </table>
                </div>

                {/* 🌟 PREMIUM COUPON & DISCOUNT ENGINE MANAGEMENT */}
                <div className="luxury-coupon-panel-box mt-5">
                  <h5 className="coupon-panel-title mb-3">Promo Code / Voucher</h5>
                  <div className="luxury-coupon-input-group d-flex">
                    <input
                      className="luxury-coupon-field flex-grow-1"
                      type="text"
                      placeholder="Enter promo code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      disabled={!!activeCoupon}
                    />
                    <button
                      type="button"
                      className={`luxury-coupon-trigger-btn ${activeCoupon ? "btn-remove-mode" : "btn-apply-mode"}`}
                      onClick={activeCoupon ? removeCoupon : () => applyCoupon()}
                    >
                      {activeCoupon ? "Remove" : "Apply"}
                    </button>
                  </div>

                  {/* Dynamic Coupon Cards Swatch Loop */}
                  {couponList && couponList.length > 0 && (
                    <div className="luxury-coupon-list-container mt-4">
                      <p className="available-offers-heading mb-3">Available Offers For You</p>
                      <div className="row g-3">
                        {couponList.map((coupon) => (
                          <div className="col-md-6 col-12" key={coupon._id}>
                            <div className={`luxury-coupon-ticket-card d-flex align-items-center justify-content-between ${activeCoupon === coupon.code ? "active-border" : ""}`}>
                              <div className="ticket-details-left">
                                <div className="ticket-badge-tag">
                                  {coupon?.discountType === "PERCENTAGE" ? `${coupon?.discountValue}% OFF` : `FLAT ₹${coupon?.discountValue}`}
                                </div>
                                <h6 className="ticket-main-title mt-2 mb-1">{coupon?.title}</h6>
                                <p className="ticket-code-caption mb-0">Use Code: <strong>{coupon?.code}</strong></p>
                              </div>
                              <button
                                type="button"
                                className={`ticket-action-inline-btn ${activeCoupon === coupon.code ? "applied-status" : ""}`}
                                onClick={activeCoupon === coupon.code ? removeCoupon : () => applyCoupon(coupon.code)}
                              >
                                {activeCoupon === coupon.code ? "Applied" : "Apply"}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN: ORDER DETAILS SIDEBAR CONTAINER */}
              <div className="col-xl-4">
                <div className="luxury-cart-sidebar-panel">
                  <h4 className="sidebar-details-title mb-4">Order Details</h4>
                  
                  <div className="summary-component-box mb-4">
                    <Summary cartItems={cartItems} cartSummary={cartSummary} />
                  </div>

                  <Link to="/checkout" className="luxury-cart-checkout-cta-btn text-center d-block w-100">
                    PROCEED TO CHECKOUT
                  </Link>
                  
                  <Link to="/shop" className="luxury-continue-shopping-link text-center d-block mt-3">
                    Continue Shopping
                  </Link>
                </div>
              </div>

            </div>
          ) : (
            /* 🌟 ELEVATED EMPTY CANVAS VIEW STATE */
            <div className="luxury-cart-page-empty-canvas text-center py-5">
              <div className="empty-bag-icon-frame mb-4">
                <ShoppingBag size={56} strokeWidth={1} color="#bbbbbb" />
              </div>
              <h3 className="empty-page-title mb-2">Your Shopping Bag is Empty</h3>
              <p className="empty-page-subtitle mb-5 mx-auto">
                Items you add to your bag will appear here. Let's find some premium formulas to match your look.
              </p>
              <Link to="/shop" className="luxury-empty-state-shop-now-btn px-5">
                CONTINUE TO SHOPPING
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Cart;